import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import {
  ACCESS_LEVEL_LABELS,
  ACCESS_OVERLAY_LABELS,
  failedPasswordRules,
} from '../shared/index.js';

import { ApiError } from '../api/client.js';
import { authApi } from '../api/endpoints.js';
import { useSession } from '../auth/session.js';
import { PageHeader } from '../components/page-header.js';
import { PasswordRules } from '../components/password-rules.js';
import { Alert, Badge, Button, Field } from '../components/ui.js';

export function AccountPage(): ReactNode {
  const { user, signOut } = useSession();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: async () => {
      const wasForced = user?.mustChangePassword ?? false;
      await signOut();

      void navigate('/sign-in', {
        replace: true,
        state: {
          notice: wasForced
            ? 'Your password is set. Sign in with it to carry on.'
            : 'Your password has changed. Every session was signed out, including this one.',
        },
      });
    },
    onError: (error: unknown) => {
      setProblem(
        error instanceof ApiError
          ? (error.issues[0]?.message ?? error.message)
          : 'The password could not be changed.',
      );
    },
  });

  if (user === null) return null;

  const mismatch = repeated.length > 0 && repeated !== newPassword;

  const ready =
    currentPassword.length > 0 &&
    failedPasswordRules(newPassword).length === 0 &&
    repeated === newPassword &&
    newPassword !== currentPassword;

  return (
    <>
      <PageHeader title="Your account" subtitle={user.email} />

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        <div className="grid max-w-[900px] gap-4 lg:grid-cols-2">
          <section className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
            <h2 className="mb-4 text-[15px] font-bold text-navy dark:text-dark-ink">
              {user.firstName} {user.lastName}
            </h2>

            <dl className="divide-y divide-hairline border-y border-hairline text-[13.5px] dark:divide-dark-line dark:border-dark-line">
              <div className="flex gap-3 py-2">
                <dt className="w-32 shrink-0 text-muted">Job role</dt>
                <dd className="text-navy dark:text-dark-ink">{user.jobRoleSlug}</dd>
              </div>
              <div className="flex gap-3 py-2">
                <dt className="w-32 shrink-0 text-muted">Email</dt>
                <dd className="min-w-0 truncate text-navy dark:text-dark-ink">{user.email}</dd>
              </div>
            </dl>

            {user.overlays.length > 0 ? (
              <div className="mt-4">
                <p className="eyebrow mb-2 text-muted">Access layers</p>
                <div className="flex flex-wrap gap-1.5">
                  {user.overlays.map((overlay) => (
                    <Badge key={overlay} tone="gold">
                      {ACCESS_OVERLAY_LABELS[overlay]}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}


            <div className="mt-4">
              <p className="eyebrow mb-2 text-muted">Departments</p>
              <ul className="flex flex-col gap-1 text-[13px]">
                {user.departments.map((entry) => (
                  <li key={entry.departmentId} className="flex items-center justify-between gap-3">
                    <span className="truncate text-navy dark:text-dark-ink">{entry.slug}</span>
                    <span className="shrink-0 text-muted">{ACCESS_LEVEL_LABELS[entry.level]}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="surface-card p-5 dark:border-dark-line dark:bg-dark-panel">
            <h2 className="mb-1 text-[15px] font-bold text-navy dark:text-dark-ink">
              Change your password
            </h2>
            <p className="mb-4 text-[13px] text-muted">
              Every other session is signed out when the password changes, so you will be asked to
              sign in again.
            </p>

            {user.mustChangePassword ? (
              <Alert tone="info">
                This account still uses the password it was created with. Choose your own before you
                carry on.
              </Alert>
            ) : null}

            {problem === null ? null : <Alert tone="error">{problem}</Alert>}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                setProblem(null);
                change.mutate();
              }}
            >
              <Field
                label="Current password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value);
                }}
              />

              <Field
                label="New password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                }}
              />

              <PasswordRules value={newPassword} />

              <Field
                label="Repeat the new password"
                type="password"
                autoComplete="new-password"
                value={repeated}
                onChange={(event) => {
                  setRepeated(event.target.value);
                }}
                {...(mismatch ? { error: 'The two do not match.' } : {})}
              />

              <Button type="submit" className="w-full" disabled={!ready} loading={change.isPending}>
                Change the password
              </Button>
            </form>
          </section>
        </div>
      </div>
    </>
  );
}
